'use client';
/**
 * components/MobileShell.tsx — OGRYN
 *
 * Chrome mobile :
 *   Bottom nav 5 boutons : Annonces | Annonce | Otto | Alertes | Mon espace
 *   FABs (home) : urgence (rouge) | filtres (loupe) | QR
 *   Panels : ListPanel (slide gauche, floating card) | MonEspaceSheet (slide droite, bounded)
 *   Overlays : OttoBanner, QRSheet, FiltersSheet
 *   Blur : quand un popup est ouvert, carte + FABs floutés (nav non floutée)
 *
 * ⚠️ Aucun import Leaflet.
 */

import { type ReactNode, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useMobile } from '@/contexts/MobileContext';
import OttoBanner     from './OttoBanner';
import QRSheet        from './QRSheet';
import FiltersSheet   from './FiltersSheet';
import MobileListView from './MobileListView';
import UrgenceSheet   from './UrgenceSheet';
import MonEspaceSheet from './MonEspaceSheet';

interface MobileShellProps { children: ReactNode }

// ponytail: bottom-nav désactivée globalement (demande user 2026-07-12). On garde
// tout le code, on ne rend juste plus la barre. Repasser SHOW_BOTTOM_NAV=true pour la réactiver.
const SHOW_BOTTOM_NAV = false;
const NAV_H = SHOW_BOTTOM_NAV ? 56 : 0;
// FABs bottom: NAV_H + 8. FABs height: 44. Popups 8px au-dessus du top des FABs.
const FAB_BOTTOM   = NAV_H + 8;
const POPUP_BOTTOM = NAV_H + 8 + 44 + 8; // au-dessus des FABs

// ── SVG Icons ─────────────────────────────────────────────────────────────────

function IconBurger({ color }: { color: string }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round">
      <line x1="3" y1="6"  x2="21" y2="6"/>
      <line x1="3" y1="12" x2="21" y2="12"/>
      <line x1="3" y1="18" x2="21" y2="18"/>
    </svg>
  );
}

function IconMapNav({ color }: { color: string }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
      <line x1="8"  y1="2"  x2="8"  y2="18"/>
      <line x1="16" y1="6"  x2="16" y2="22"/>
    </svg>
  );
}

function IconPlus({ color }: { color: string }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
    </svg>
  );
}

function IconRobot({ color }: { color: string }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="8" width="18" height="13" rx="2"/>
      <path d="M12 8V5"/><circle cx="12" cy="4" r="1.5" fill={color}/>
      <circle cx="8.5"  cy="14" r="1.5" fill={color} stroke="none"/>
      <circle cx="15.5" cy="14" r="1.5" fill={color} stroke="none"/>
      <path d="M9 18h6"/><path d="M3 13H1.5"/><path d="M21 13h1.5"/>
    </svg>
  );
}

function IconBell({ color }: { color: string }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  );
}

function IconUser({ color }: { color: string }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  );
}

function IconLoupe() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  );
}

function IconQR() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="7" height="7" rx="1" stroke="white" strokeWidth="1.5"/>
      <rect x="3" y="3" width="3" height="3" rx="0.5" fill="white"/>
      <rect x="12" y="1" width="7" height="7" rx="1" stroke="white" strokeWidth="1.5"/>
      <rect x="14" y="3" width="3" height="3" rx="0.5" fill="white"/>
      <rect x="1" y="12" width="7" height="7" rx="1" stroke="white" strokeWidth="1.5"/>
      <rect x="3" y="14" width="3" height="3" rx="0.5" fill="white"/>
      <rect x="10" y="10" width="2" height="2" fill="white"/><rect x="13" y="10" width="2" height="2" fill="white"/>
      <rect x="16" y="10" width="2" height="2" fill="white"/><rect x="10" y="13" width="2" height="2" fill="white"/>
      <rect x="13" y="13" width="2" height="2" fill="white"/><rect x="10" y="16" width="5" height="2" fill="white"/>
      <rect x="16" y="16" width="2" height="2" fill="white"/>
    </svg>
  );
}

function IconZap() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  );
}

// ── Nav item ──────────────────────────────────────────────────────────────────

interface NavItemProps {
  icon:    ReactNode;
  label:   string;
  active:  boolean;
  onClick: () => void;
  special?: boolean;
}

function NavItem({ icon, label, active, onClick, special = false }: NavItemProps) {
  const color = special ? '#fca5a5' : active ? '#2E7D52' : 'rgba(255,255,255,.38)';
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ flex: 1, height: NAV_H, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, background: 'none', border: 'none', cursor: 'pointer' }}
    >
      {icon}
      <span style={{ fontSize: 7, fontWeight: 500, color }}>{label}</span>
    </button>
  );
}

// ── Shell ─────────────────────────────────────────────────────────────────────

export default function MobileShell({ children }: MobileShellProps) {
  const pathname  = usePathname();
  const router    = useRouter();
  const [urgenceOpen,   setUrgenceOpen]   = useState(false);
  const [monEspaceOpen, setMonEspaceOpen] = useState(false);
  const {
    viewMode,    setViewMode,
    ottoOpen,   setOttoOpen,
    filtersOpen, setFiltersOpen,
    setQrOpen,
  } = useMobile();

  // FABs héritées de RapidPro (urgence rouge / filtres / QR) : plus rien à faire
  // sur OGRYN — sur mobile, l'app EST le proto /app (cf. redirection dans
  // HomeLanding). Le code reste en place, il ne se rend plus.
  // ponytail: repasser SHOW_LEGACY_FABS=true pour les revoir, ou les supprimer
  // définitivement une fois la home mobile refaite.
  const SHOW_LEGACY_FABS = false;
  const isHome   = SHOW_LEGACY_FABS && pathname === '/';
  const listOpen = isHome && viewMode === 'list';
  // Écrans plein écran / pré-connexion : masque la bottom nav (sinon elle passe
  // au-dessus du contenu). Couvre le proto /app et le flux accueil + auth.
  const CHROMELESS = ['/particulier', '/bienvenue', '/login', '/signup', '/onboarding'];
  const hideNav  = CHROMELESS.some(p => pathname === p || pathname.startsWith(`${p}/`));

  /* Ferme tous les petits sheets/popups */
  function closeAllSheets() {
    setOttoOpen(false);
    setQrOpen(false);
    setFiltersOpen(false);
    setUrgenceOpen(false);
  }

  /* Ferme les panels (liste + mon espace) */
  function closePanels() {
    if (isHome && viewMode === 'list') setViewMode('map');
    setMonEspaceOpen(false);
  }

  function toggleView() {
    closeAllSheets();
    setMonEspaceOpen(false); // mutual exclusion
    if (isHome) {
      setViewMode(viewMode === 'map' ? 'list' : 'map');
    } else {
      router.push('/');
    }
  }

  function openFilters() {
    // Filtres : ferme les autres sheets mais PAS le panel liste
    setOttoOpen(false);
    setQrOpen(false);
    setUrgenceOpen(false);
    setFiltersOpen(true);
  }

  function openQr() {
    closeAllSheets();
    closePanels();
    setQrOpen(true);
  }

  function toggleOtto() {
    const next = !ottoOpen;
    closeAllSheets();
    closePanels();
    setOttoOpen(next);
  }

  function toggleMonEspace() {
    const next = !monEspaceOpen;
    closeAllSheets();
    if (next && isHome && viewMode === 'list') setViewMode('map'); // mutual exclusion
    setMonEspaceOpen(next);
  }

  /* Blur : quand un popup est ouvert OU le panel liste (hors QR qui a son propre backdrop dark) */
  const blurOpen = ottoOpen || urgenceOpen || filtersOpen || listOpen;

  const fabBase = (accent = false): React.CSSProperties => ({
    width: 44, height: 44, borderRadius: 13,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(0,0,0,.55)',
    background: accent ? '#2E7D52' : 'rgba(10,7,4,.92)',
    border: accent ? 'none' : '0.5px solid rgba(255,255,255,.16)',
  });

  return (
    // Bug WebKit iOS standalone : innerHeight rapporté = écran − safe-area-inset-top
    // (879 au lieu de 926) → fixed inset:0 laisse 47pt découverts en bas. Le fix :
    // hauteur explicite --actual-vh calculée en JS (bootstrap inline dans
    // app/layout.tsx), appliquée ici UNIQUEMENT en standalone (en onglet navigateur,
    // inset:0 seul reste la bonne mesure). Fallback 100dvh = comportement actuel
    // tant que le bootstrap n'a pas tourné. Si Apple corrige le bug, --actual-vh
    // retombe sur innerHeight natif et tout reste correct sans toucher au code.
    <div className="fixed inset-0 overflow-hidden bg-[#2D2D2D] [@media(display-mode:standalone)]:h-[var(--actual-vh,100dvh)]">

      {/* ── Contenu principal ── */}
      {isHome ? (
        /* La carte remplit tout — position absolute inset:0 pour hauteur correcte */
        <div style={{ position: 'absolute', inset: 0 }}>{children}</div>
      ) : (
        <div style={{ height: '100%', overflowY: 'auto', paddingBottom: NAV_H }}>{children}</div>
      )}

      {/* ── Blur overlay — derrière les popups, devant la carte + FABs, au-dessus nav interdit ── */}
      {blurOpen && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0,
          bottom: NAV_H,
          zIndex: 693,
          backdropFilter: 'blur(5px)',
          WebkitBackdropFilter: 'blur(5px)',
          background: 'rgba(0,0,0,.22)',
          pointerEvents: 'none',
        }} />
      )}

      {/* ── Panel liste — floating card, slide depuis la gauche (home uniquement) ── */}
      {isHome && (
        <div style={{
          position:   'fixed',
          top:        'calc(env(safe-area-inset-top) + 52px)',
          bottom:     `${POPUP_BOTTOM}px`,
          left:       12,
          right:      12,
          transform:  listOpen ? 'translateX(0)' : 'translateX(-110%)',
          transition: 'transform .3s cubic-bezier(.16,1,.3,1)', // sans rebond (le 1.2 d'avant dépassait — signalé le 16/08)
          zIndex:     695, // au-dessus du blur (693) → panneau net + cliquable, sous FAB Filtres (696) et nav (700)
          background: '#0f0d0b',
          display:    'flex',
          flexDirection: 'column',
          borderRadius: 18,
          border: '0.5px solid rgba(255,255,255,.10)',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0,0,0,.6)',
        }}>
          {/* Contenu scrollable */}
          <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
            <MobileListView inPanel />
          </div>
        </div>
      )}

      {/* ── Panel Mon espace — hérité RapidPro, plus monté (espace Compte dans l'app) ── */}
      {SHOW_LEGACY_FABS && <MonEspaceSheet open={monEspaceOpen} onClose={() => setMonEspaceOpen(false)} />}

      {/* ── FABs (home uniquement) — toujours visibles sur home ──
           Urgence + QR : z-index 600 (sous le blur → floutés quand popup ouvert)
           Filtres      : z-index 696 (au-dessus du blur → toujours net) */}

      {/* Urgence — gauche, sous le blur */}
      {isHome && (
        <div style={{ position: 'fixed', bottom: `calc(env(safe-area-inset-bottom) + ${FAB_BOTTOM}px)`, left: 10, zIndex: 600 }}>
          <div
            style={{
              ...fabBase(),
              background: 'rgba(220,38,38,.85)',
              border: '0.5px solid rgba(220,38,38,.6)',
              boxShadow: '0 4px 14px rgba(220,38,38,.5)',
            }}
            onClick={() => { closeAllSheets(); closePanels(); setUrgenceOpen(true); }}
            title="Intervention urgente"
          >
            <IconZap />
          </div>
        </div>
      )}

      {/* Filtres — centre, au-dessus du blur */}
      {isHome && (
        <div style={{ position: 'fixed', bottom: `calc(env(safe-area-inset-bottom) + ${FAB_BOTTOM}px)`, left: '50%', transform: 'translateX(-50%)', zIndex: 696 }}>
          <div
            style={{ ...fabBase(), ...(filtersOpen ? { border: '0.5px solid rgba(5,150,105,.4)', background: 'rgba(5,150,105,.12)' } : {}) }}
            onClick={openFilters}
          >
            <IconLoupe />
          </div>
        </div>
      )}

      {/* QR — droite, sous le blur */}
      {isHome && (
        <div style={{ position: 'fixed', bottom: `calc(env(safe-area-inset-bottom) + ${FAB_BOTTOM}px)`, right: 10, zIndex: 600 }}>
          <div style={{ ...fabBase(true) }} onClick={openQr}>
            <IconQR />
          </div>
        </div>
      )}

      {/* ── Overlays hérités de RapidPro (bannière, QR, filtres artisans, urgence) :
            plus montés sur OGRYN — code conservé derrière SHOW_LEGACY_FABS ── */}
      {SHOW_LEGACY_FABS && (
        <>
          <OttoBanner />
          <QRSheet />
          <FiltersSheet />
          {urgenceOpen && <UrgenceSheet onClose={() => setUrgenceOpen(false)} />}
        </>
      )}

      {/* ── Bottom nav — 5 boutons, hauteur fixe (pas d'env pour garder les boutons en bas) ── */}
      {SHOW_BOTTOM_NAV && !hideNav && (
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        height: NAV_H,
        zIndex: 700,
        background: 'rgba(8,5,2,.97)',
        borderTop: '0.5px solid rgba(255,255,255,.07)',
        display: 'flex', alignItems: 'center',
        padding: '0 4px',
      }}>

        {/* Annonces / Carte */}
        <NavItem
          icon={listOpen
            ? <IconMapNav color="#2E7D52" />
            : <IconBurger color={listOpen ? '#2E7D52' : 'rgba(255,255,255,.38)'} />}
          label={listOpen ? 'Carte' : 'Annonces'}
          active={listOpen}
          onClick={toggleView}
        />

        {/* Otto */}
        <button
          type="button"
          onClick={toggleOtto}
          style={{
            flex: 1, height: 44, margin: '0 4px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
            background: ottoOpen ? 'rgba(220,38,38,.30)' : 'rgba(180,20,20,.22)',
            border: `0.5px solid ${ottoOpen ? 'rgba(220,38,38,.55)' : 'rgba(220,38,38,.35)'}`,
            borderRadius: 10, cursor: 'pointer', transition: 'all .15s',
          }}
        >
          <IconRobot color={ottoOpen ? '#fca5a5' : '#f87171'} />
          <span style={{ fontSize: 7, fontWeight: 700, color: '#fca5a5' }}>Otto</span>
        </button>

        {/* Mon espace */}
        <NavItem
          icon={<IconUser color={monEspaceOpen ? '#2E7D52' : 'rgba(255,255,255,.38)'} />}
          label="Mon espace"
          active={monEspaceOpen}
          onClick={toggleMonEspace}
        />
      </div>
      )}
    </div>
  );
}
